import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  baseStyle,
  sizeStyles,
  variantStyles,
  variantHoverStyles,
  variantActiveStyles,
  variantDisabledStyles,
  disabledStyle,
  loadingStyle,
  VARIANTS,
  SIZES,
} from "./button.style.js";

const ICON_SLOT_SIZE = 14;

function LoadingSpinner() {
  return (
    <svg
      width={ICON_SLOT_SIZE}
      height={ICON_SLOT_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" strokeDasharray="45" strokeDashoffset="12" opacity="0.9">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

const iconSlotStyle = {
  width: ICON_SLOT_SIZE,
  minWidth: ICON_SLOT_SIZE,
  height: ICON_SLOT_SIZE,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

/**
 * Button component – variant and size driven via style objects from button.style.js.
 * Supports all Html.css button variants. Uses forwardRef for ref access to the native button.
 *
 * @param {string} [variant='primary'] - See VARIANTS in button.style.js
 * @param {string} [size='md'] - sm | md | lg
 * @param {boolean} [disabled=false]
 * @param {boolean} [loading=false]
 * @param {React.ReactNode} [leftIcon]
 * @param {React.ReactNode} [rightIcon]
 */
const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    disabled = false,
    loading = false,
    leftIcon,
    rightIcon,
    type = "button",
    onClick,
    className,
    style,
    children,
    tabIndex: tabIndexProp,
    ...rest
  },
  ref
) {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isDisabled = disabled || loading;
  const tabIndex = isDisabled ? -1 : tabIndexProp !== undefined ? tabIndexProp : undefined;
  const v = VARIANTS.includes(variant) ? variant : "primary";
  const s = SIZES.includes(size) ? size : "md";

  const buttonStyle = useMemo(() => {
    return {
      ...baseStyle,
      ...sizeStyles[s],
      ...(variantStyles[v] || variantStyles.primary),
      ...(isHovered && !isDisabled ? variantHoverStyles[v] || {} : {}),
      ...(isActive && !isDisabled ? variantActiveStyles[v] || {} : {}),
      ...(isDisabled ? variantDisabledStyles[v] || disabledStyle : {}),
      ...(loading ? loadingStyle : {}),
      ...(style || {}),
    };
  }, [v, s, isHovered, isActive, isDisabled, loading, style]);

  const handleClick = useCallback(
    (e) => {
      if (isDisabled) return;
      if (e.button !== undefined && e.button !== 0) return;
      onClick?.(e);
    },
    [isDisabled, onClick]
  );

  const hasLeftSlot = leftIcon || loading;
  const hasRightSlot = rightIcon && !loading;

  const content = (
    <>
      {hasLeftSlot && <span style={iconSlotStyle}>{loading ? <LoadingSpinner /> : leftIcon}</span>}
      {children != null && <span>{children}</span>}
      {hasRightSlot && <span style={iconSlotStyle}>{rightIcon}</span>}
    </>
  );

  return (
    <button
      ref={ref}
      type={type}
      className={className}
      style={buttonStyle}
      disabled={isDisabled}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}
      onMouseDown={(e) => e.button === 0 && !isDisabled && setIsActive(true)}
      onMouseUp={(e) => e.button === 0 && setIsActive(false)}
      aria-busy={loading}
      {...(tabIndex !== undefined ? { tabIndex } : {})}
      {...rest}
    >
      {content}
    </button>
  );
});

Button.propTypes = {
  variant: PropTypes.oneOf(VARIANTS),
  size: PropTypes.oneOf(SIZES),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  type: PropTypes.oneOf(["button", "submit"]),
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
  tabIndex: PropTypes.number,
};

Button.displayName = "Button";

export default Button;
